<template>
    <div>
        <v-row>
            <v-col cols="6">
                <TranslatableTextField
                    :valueProp="table.titles"
                    fieldTitle="Title"
                    @changeValue="handleTranslatedValueChange($event, 'titles', 'title')"
                />
            </v-col>
            <v-col cols="6">
                <TranslatableTextField
                    :valueProp="table.subtitles"
                    fieldTitle="Subtitle"
                    @changeValue="handleTranslatedValueChange($event, 'subtitles', 'subtitle')"
                />
            </v-col>
        </v-row>
        <v-row class="align-end">
            <v-col cols="6">
                <v-select
                    :items="formulaTypes"
                    v-model="table.formula_type"
                    label="Formula type"
                    item-text="name"
                    item-value="type"
                    outlined
                    dense
                    hide-details
                    placeholder="Formula type"
                />
            </v-col>

            <v-col class="d-flex align-end" cols="2"
                   v-if="table.formula_type && ['recommendation', 'boolean'].includes(table.formula_type) && !table.show_variable_as_result">
                <TranslatableTextField
                    :valueProp="table.boolean_yes_answers"
                    fieldTitle="Boolean YES"
                    @changeValue="handleTranslatedValueChange($event, 'boolean_yes_answers')"
                />

            </v-col>
            <v-col cols="2"
                   v-if="table.formula_type && ['recommendation', 'boolean'].includes(table.formula_type) && !table.show_variable_as_result">
                <TranslatableTextField
                    :valueProp="table.boolean_no_answers"
                    fieldTitle="Boolean NO"
                    @changeValue="handleTranslatedValueChange($event, 'boolean_no_answers')"
                />
            </v-col>
            <v-col cols="2"
                   v-if="table.formula_type && ['recommendation', 'boolean'].includes(table.formula_type) && table.show_variable_as_result">
                <v-text-field
                    label="Variable as result"
                    outlined
                    dense
                    hide-details
                    placeholder="Variable as result"
                    v-model="table.variable_as_result"
                ></v-text-field>
            </v-col>
            <v-col cols="2"
                   v-if="table.formula_type && ['recommendation', 'boolean'].includes(table.formula_type) && table.show_variable_as_result">
                <v-select
                    :items="variableAsResultMethods"
                    v-model="table.variable_as_result_method"
                    label="Method"
                    item-text="name"
                    item-value="value"
                    outlined
                    dense
                    hide-details
                    placeholder="Method"
                />
            </v-col>
            <v-col cols="2" v-if="table.formula_type && ['recommendation', 'boolean'].includes(table.formula_type)">
                <v-checkbox hide-details class="mb-2" v-model="table.show_variable_as_result"
                            label="Show variable as result"></v-checkbox>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-text-field
                    label="Formula"
                    outlined
                    dense
                    hide-details
                    placeholder="Formula"
                    v-model="table.formula"
                    v-if="table.formula_type && ['value', 'boolean'].includes(table.formula_type)"
                ></v-text-field>
                <v-select
                    :items="calculations"
                    v-model="table.calculation_idx"
                    label="Calculation index"
                    item-text="name"
                    item-value="type"
                    outlined
                    dense
                    hide-details
                    placeholder="Calculation index"
                    v-else
                />
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="3">
                <v-menu
                    v-model="showStartDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="formatDate(table.start_date)"
                            append-icon="mdi-calendar"
                            class="min-width-115"
                            readonly
                            label="Start date"
                            outlined
                            dense
                            hide-details
                            placeholder="Start date"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        first-day-of-week="1"
                        v-model="table.start_date"
                        @input="showStartDateMenu = false"
                    ></v-date-picker>
                </v-menu>
            </v-col>
            <v-col cols="3">
                <v-menu
                    v-model="showEndDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            :value="formatDate(table.end_date)"
                            append-icon="mdi-calendar"
                            class="min-width-115"
                            readonly
                            label="End date"
                            outlined
                            dense
                            hide-details
                            placeholder="End date"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        first-day-of-week="1"
                        v-model="table.end_date"
                        @input="showEndDateMenu = false"
                    ></v-date-picker>
                </v-menu>
            </v-col>
            <v-col cols="3">
                <v-select
                    :items="icons"
                    v-model="table.icon"
                    label="Icon"
                    outlined
                    dense
                    hide-details
                    placeholder="Icon"
                />
            </v-col>
            <v-col cols="3">
                <v-text-field
                    label="Decimal places for value"
                    outlined
                    dense
                    hide-details
                    placeholder="Decimal places for value"
                    v-model="table.value_decimal_places"
                ></v-text-field>
            </v-col>
        </v-row>

        <v-row justify="center" align="center">
            <v-col>
                <v-switch v-model="table.has_graph" label="Calculate value per day"></v-switch>
            </v-col>
            <v-col>
                <v-checkbox v-model="table.ignore_zero_values" label="Ignore zero values"></v-checkbox>
            </v-col>
        </v-row>

        <v-row justify="center" align="center">
            <v-col>
                <v-btn class="mr-6" color="success" @click="handleSaveClick(table.id)">Save</v-btn>
            </v-col>
            <v-spacer/>
            <v-col class="d-flex justify-end">
                <v-btn color="mr-6 primary" @click="handleRecalculationClick(table.id)">Recalculate</v-btn>
                <v-btn color="error" @click="handleDeleteClick(table.id)">Delete</v-btn>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import moment from 'moment/moment';
import { mapState } from 'vuex';
import TranslatableTextField from '../TranslatableTextField.vue';

export default {
    name: 'AnnualSummaryBlockTableItemsComponent',
    components: {TranslatableTextField},
    data() {
        return {
            showStartDateMenu: false,
            showEndDateMenu: false,
            titleLanguageSelect: 'en',
            subtitleLanguageSelect: 'en',
            formulaBooleanYesLanguage: 'en',
            formulaBooleanNoLanguage: 'en',
            formulaTypes: [
                {
                    name: 'Value',
                    type: 'value',
                },
                {
                    name: 'Boolean',
                    type: 'boolean',
                },
                {
                    name: 'Recommendation',
                    type: 'recommendation',
                },
            ],
            variableAsResultMethods: [
                {
                    name: 'Average',
                    value: 'average',
                },
                {
                    name: 'Median',
                    value: 'median',
                },
                {
                    name: "Min",
                    value: "min"
                },
                {
                    name: "Max",
                    value: "max"
                },
                {
                    name: "Sum",
                    value: "sum"
                }
            ],
        };
    },
    computed: {
        ...mapState('annualSummary', ['icons', 'calculations']),
    },
    props: {
        table: {
            default: {},
        },
    },
    methods: {
        handleTranslatedValueChange(e, prop, enProp = null) {
            this.table[prop][e.language] = e.value;

            if (enProp && e.language === 'en') {
                this.table[enProp] = e.value;
            }
        },
        handleSaveClick(tableId) {
            this.$emit('save', tableId);
        },
        formatDate(date) {
            if (date) {
                return moment(date).format('DD.MM.YY');
            }
            return '-';
        },
        handleDeleteClick(tableId) {
            if (confirm(`Are you sure that you want to delete table ID ${tableId}?`)) {
                this.$emit('delete', tableId);
            }
        },
        handleRecalculationClick(tableId) {
            if (confirm(`Are you sure that you want to recalculate table ID ${tableId} last week?`)) {
                this.$emit('recalculate', tableId);
            }
        },
    },
};
</script>

<style scoped></style>
